import type { AlgoliaProductCard, PricelistItem, ProductDetails } from "@raumagarn/formatters";
import type { BaseSelectionItem } from "@trydig/centra-types/selection";
import { getUserData } from "~/lib/gtm/hooks";
import type { GTMEventData, GTMProduct, PageView } from "~/lib/gtm/types";

export const sendGTMEvent = (data: GTMEventData | PageView) => {
	if (typeof window === "undefined") return;

	window.dataLayer ||= [];

	window.dataLayer.push({
		ecommerce: null,
	});
	const userData = getUserData();
	const payload = userData ? { ...data, user_data: userData } : data;
	window.dataLayer.push(payload);
};

export const productDetailsToGTMProduct = ({
	product,
	price,
	currency,
}: {
	product: ProductDetails;
	price: PricelistItem;
	currency: string;
}): GTMProduct => {
	const categories = product.categoryName
		? Object.fromEntries(
				product.categoryName
					.map((name, i) => [`item_category${i === 0 ? "" : i + 1}`, name])
					.slice(0, 4),
			)
		: {};

	return {
		quantity: 1,
		item_id: product.sku as string,
		item_name: product.name as string,
		price: Math.floor(price?.priceAsNumber ?? 0),
		affiliation: "Website",
		currency,
		discount: Math.ceil((price?.priceBeforeDiscountAsNumber ?? 0) - (price?.priceAsNumber ?? 0)),
		item_brand: "Raumagarn",
		...categories,
		item_variant: product.variantName,
	};
};

export const productCardsToGTMProducts = ({
	products,
	currency,
	categoryPage,
}: {
	products: AlgoliaProductCard[];
	currency: string;
	categoryPage: string;
}): GTMProduct[] => {
	return products.map((product, index) =>
		productCardToGTMProduct({ product, index, currency, categoryPage }),
	);
};

export const productCardToGTMProduct = ({
	product,
	index,
	currency,
	categoryPage,
}: {
	product: AlgoliaProductCard;
	index: number;
	currency: string;
	categoryPage: string;
}): GTMProduct => {
	const categories = product.categories
		? Object.fromEntries(
				product.categories
					.map((name, i) => [`item_category${i === 0 ? "" : i + 1}`, name])
					.slice(0, 4),
			)
		: {};

	return {
		item_id: product.sku as string,
		item_name: product.name as string,
		price: Math.floor(product?.priceAsNumber ?? 0),
		affiliation: "Website",
		currency,
		discount: Math.ceil(
			(product?.priceBeforeDiscountAsNumber ?? 0) - (product?.priceAsNumber ?? 0),
		),
		item_brand: "Raumagarn",
		...categories,
		index,
		item_variant: product.variantName,
		item_list_name: categoryPage,
	};
};

export const selectionItemsToGTMProducts = ({
	items,
	currency,
}: {
	items: BaseSelectionItem[];
	currency: string;
}): GTMProduct[] => {
	return items.map((item, index) =>
		selectionItemToGTMProduct({
			item,
			currency,
			index,
		}),
	);
};

export const selectionItemToGTMProduct = ({
	item,
	index,
	currency,
}: {
	item: BaseSelectionItem;
	index?: number;
	currency: string;
}): GTMProduct => {
	const categories = item.product?.categoryName
		? Object.fromEntries(
				item.product.categoryName
					.map((name, i) => [`item_category${i === 0 ? "" : i + 1}`, name])
					.slice(0, 4),
			)
		: {};

	return {
		item_id: item.product?.sku as string,
		item_name: item.product?.name as string,
		price: Math.floor(item.product?.priceAsNumber ?? 0),
		affiliation: "Website",
		currency,
		discount: Math.ceil(
			(item.product?.priceBeforeDiscountAsNumber ?? 0) - (item.product?.priceAsNumber ?? 0),
		),
		item_brand: "Raumagarn",
		...categories,
		index,
		item_variant: item.product?.variantName,
		quantity: item.quantity,
	};
};
